import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FleetTabs, PageHeader, NewCampaignModal, Pill, CAMPAIGN_COLORS, JOB_COLORS, fmtDate, errText, useLiveRefresh } from './common';

export function describeSelector(sel) {
  if (!sel) return '';
  if (sel.rollback_of) return `rollback of #${sel.rollback_of}`;
  if (sel.all) return 'all servers';
  if (sel.hosts) return sel.hosts.length <= 3 ? sel.hosts.join(', ') : `${sel.hosts.length} servers`;
  if (sel.tags) return `tags: ${sel.tags.join(', ')}`;
  if (sel.status) return `status: ${sel.status.join(', ')}`;
  return JSON.stringify(sel);
}

export function JobBar({ jobs, total }) {
  if (!total) return null;
  const order = ['succeeded', 'failed', 'timed_out', 'dispatched', 'queued', 'pending', 'cancelled'];
  return (
    <div style={{ minWidth: 180 }}>
      <div style={{ display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden', background: 'var(--border)' }}>
        {order.map(s => jobs[s] ? <div key={s} title={`${s}: ${jobs[s]}`} style={{ width: `${(100 * jobs[s]) / total}%`, background: JOB_COLORS[s] }} /> : null)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
        {order.filter(s => jobs[s]).map(s => `${jobs[s]} ${s.replace('_', ' ')}`).join(' · ')}
      </div>
    </div>
  );
}

export default function FleetCampaigns() {
  const navigate = useNavigate();
  const [camps, setCamps] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try { const r = await axios.get('/cve-fleet/campaigns'); setCamps(r.data.campaigns); setError(''); }
    catch (e) { setError(errText(e, 'Failed to load campaigns')); }
    finally { if (!silent) setLoading(false); }
  }, []);
  useEffect(() => { load(); axios.get('/cve-fleet/hosts').then(r => setTags(r.data.tags)).catch(() => {}); }, [load]);
  const active = camps.some(c => c.state === 'running');
  useLiveRefresh(() => load(true), 10000, active);

  return (
    <div>
      <PageHeader />
      <div className="page-body">
        <FleetTabs />
        <div className="toolbar">
          {active && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>● live — refreshing every 10s</span>}
          <div className="spacer" />
          <button className="btn btn-primary" style={{ marginRight: 8 }} onClick={() => setShowNew(true)}>New campaign…</button>
          <button className="btn btn-secondary" onClick={() => load()} disabled={loading}>Refresh</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
        {loading ? <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
          : camps.length === 0 ? <div className="empty-state"><h3>No campaigns yet</h3><p>Start with a detect campaign across all servers.</p></div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
                  <th style={{ padding: '8px 10px' }}>#</th><th style={{ padding: '8px 10px' }}>Action</th>
                  <th style={{ padding: '8px 10px' }}>Servers</th><th style={{ padding: '8px 10px' }}>State</th>
                  <th style={{ padding: '8px 10px' }}>Progress</th><th style={{ padding: '8px 10px' }}>By</th>
                  <th style={{ padding: '8px 10px' }}>Started</th></tr></thead>
                <tbody>{camps.map(c => (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--border)', fontSize: 13, cursor: 'pointer' }} onClick={() => navigate(`/cve-fleet/campaigns/${c.id}`)}>
                    <td style={{ padding: 10 }}>{c.id}</td>
                    <td style={{ padding: 10 }}><strong>{c.action}</strong>{c.note && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.note}</div>}</td>
                    <td style={{ padding: 10 }}>{describeSelector(c.selector)} <span style={{ color: 'var(--text-muted)' }}>({c.total})</span></td>
                    <td style={{ padding: 10 }}><Pill label={c.state} color={CAMPAIGN_COLORS[c.state]} />{c.reason && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{c.reason}</div>}</td>
                    <td style={{ padding: 10 }}><JobBar jobs={c.jobs} total={c.total} /></td>
                    <td style={{ padding: 10, fontSize: 12 }}>{c.created_by}</td>
                    <td style={{ padding: 10, whiteSpace: 'nowrap' }}>{fmtDate(c.created_at)}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
      </div>
      {showNew && <NewCampaignModal tags={tags} onClose={() => setShowNew(false)} onCreated={c => navigate(`/cve-fleet/campaigns/${c.id}`)} />}
    </div>
  );
}
